import { forwardRef, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * ServicePickCard — выбираемая карточка услуги (`.svc-card` макета): иконка, заголовок, описание.
 * Состояния default/hover/focus/selected. selected передаётся AT (aria-pressed). Выбор с клавиатуры.
 * Чтобы избежать сдвига от border 1px↔2px макета, выделение selected — кольцом (ring) из токена.
 */
export interface ServicePickCardProps {
  icon?: ReactNode
  title: string
  description?: string
  selected?: boolean
  onSelect?: () => void
  disabled?: boolean
  className?: string
}

export const ServicePickCard = forwardRef<HTMLButtonElement, ServicePickCardProps>(
  ({ icon, title, description, selected, onSelect, disabled, className }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'rounded-12 border bg-white p-4 text-left outline-none transition-colors',
        'hover:border-accent hover:bg-accent-bg',
        'focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        'disabled:cursor-not-allowed disabled:opacity-50',
        selected ? 'border-accent bg-accent-bg ring-1 ring-accent' : 'border-line-strong',
        className,
      )}
    >
      {icon ? (
        <span className="mb-2.5 flex size-brand-lg items-center justify-center rounded-9 bg-gray-bg text-txt-secondary">
          {icon}
        </span>
      ) : null}
      <span className="block font-semibold text-txt">{title}</span>
      {description ? (
        <span className="mt-1 block text-12 text-txt-secondary">{description}</span>
      ) : null}
    </button>
  ),
)
ServicePickCard.displayName = 'ServicePickCard'
